import { getDeliveryOptions } from '@/data/api/products/requests';
import { languageMapServiceLayer } from '.';
import i18n from '@/languages/i18n';
import { Warehouse } from '@/warehouse';
import dayjs, { Dayjs } from 'dayjs';
import { useAccountStore } from '@/stores/account';
import { WarehouseCalendar } from './warehouse';

export type DeliveryOptionDescriptionLanguage = {
  name: string;
  description1: string;
  description2: string;
  language: string;
};

export interface DeliveryOption {
  division: string[];
  country: string;
  deliveryType: string;
  deliveryTerms: string;
  deliveryIndicator: string[];
  defaultDeliveryIndicator: string;
  deliveryCategory: 'Delivery' | 'Pickup' | 'ReturnDelivery' | 'ReturnPickup';
  m3OrderOnly: boolean;
  backofficeOnly: boolean;
  // maxLoadMeter: string;
  // maxWeight: string;
  // maxVolume: string;
  leadTime: string[];
  breakingPointKPAndM3: string[];
  groupID: number;
  deliveryTypeDescriptionLanguages: DeliveryOptionDescriptionLanguage[];
  deliveryTermsDescriptionLanguages: DeliveryOptionDescriptionLanguage[];

  description(language?: keyof typeof languageMapServiceLayer): {
    terms: DeliveryOptionDescriptionLanguage;
    type: DeliveryOptionDescriptionLanguage;
  };

  getBreakPoint(isM3Order?: boolean): Dayjs | null;
  getLeadTime(isM3Order?: boolean): number;
  calcLeadTime(
    type: 'Date' | 'Popup',
    warehouse: Warehouse,
    zipCode?: string,
    countFromDate?: Dayjs | Date | string,
    isM3Order?: boolean
  ): Dayjs;
}

export class DeliveryOptionClass implements DeliveryOption {
  division: string[];
  country: string;
  deliveryType: string;
  deliveryTerms: string;
  deliveryIndicator: string[];
  defaultDeliveryIndicator: string;
  deliveryCategory: 'Delivery' | 'Pickup' | 'ReturnDelivery' | 'ReturnPickup';
  m3OrderOnly: boolean;
  backofficeOnly: boolean;
  // maxLoadMeter: string;
  // maxWeight: string;
  // maxVolume: string;
  leadTime: string[];
  breakingPointKPAndM3: string[];
  groupID: number;
  deliveryTypeDescriptionLanguages: DeliveryOptionDescriptionLanguage[];
  deliveryTermsDescriptionLanguages: DeliveryOptionDescriptionLanguage[];

  constructor(attributes: DeliveryOption) {
    this.division = attributes.division;
    this.country = attributes.country;
    this.deliveryType = attributes.deliveryType;
    this.deliveryTerms = attributes.deliveryTerms;
    this.deliveryIndicator = attributes.deliveryIndicator;
    this.defaultDeliveryIndicator = attributes.defaultDeliveryIndicator;
    this.deliveryCategory = attributes.deliveryCategory;
    this.m3OrderOnly = attributes.m3OrderOnly;
    this.backofficeOnly = attributes.backofficeOnly;
    // this.maxLoadMeter = attributes.maxLoadMeter;
    // this.maxWeight = attributes.maxWeight;
    // this.maxVolume = attributes.maxVolume;
    this.leadTime = attributes.leadTime;
    this.breakingPointKPAndM3 = attributes.breakingPointKPAndM3;
    this.groupID = attributes.groupID;
    this.deliveryTypeDescriptionLanguages = attributes.deliveryTypeDescriptionLanguages;
    this.deliveryTermsDescriptionLanguages = attributes.deliveryTermsDescriptionLanguages;
  }

  description(languageCode: keyof typeof languageMapServiceLayer = i18n.global.locale.value) {
    const lang = languageMapServiceLayer[languageCode];
    if (!lang) {
      throw new Error(`Language not found: ${languageCode}`);
    }

    // Find the description in the respective language
    // or fallback to the default option of just displaying the name and empty descriptions
    const terms = this.deliveryTermsDescriptionLanguages.find((desc) => desc.language === lang) || {
      name: this.deliveryTerms,
      description1: '',
      description2: '',
      language: '',
    };
    const type = this.deliveryTypeDescriptionLanguages.find((desc) => desc.language === lang) || {
      name: this.deliveryType,
      description1: '',
      description2: '',
      language: '',
    };
    return { terms, type };
  }

  getBreakPoint(isM3Order: boolean = false): Dayjs | null {
    const breakPoint = this.breakingPointKPAndM3.length > 0 ? this.breakingPointKPAndM3[isM3Order ? 1 : 0] : null;
    if (!breakPoint) return null;
    return dayjs().set('hour', parseInt(breakPoint)).set('minute', 0).set('second', 0);
  }

  getLeadTime(isM3Order: boolean = false): number {
    const breakPoint = this.getBreakPoint(isM3Order);
    if (!breakPoint || !this.leadTime.length) {
      // console.warn('No lead time or break point found');
      return 0;
    }
    const isBeforeBreakPoint = dayjs().isBefore(breakPoint);
    return parseInt(isBeforeBreakPoint ? this.leadTime[0] : this.leadTime[1]);
  }

  static calcNumDaysAhead(date: Dayjs, warehouse: Warehouse): number {
    const pickuptimes = new WarehouseCalendar(warehouse).pickupTimes({ includeNextOpenDay: true });

    if (dayjs(pickuptimes[0].date).isAfter(date)) {
      return 0;
    }

    let nDays = 1;
    const target = dayjs(date);
    while (dayjs(pickuptimes[nDays].date).isAfter(target) == false) {
      nDays++;
    }

    return nDays;
  }

  calcLeadTime(
    type: 'Date' | 'Popup',
    warehouse: Warehouse,
    zipCode: string = '',
    countFromDate: Dayjs | Date | string | undefined = undefined,
    isM3Order: boolean = false
  ): Dayjs {
    const leadTimeOffset = type === 'Date' ? 0 : -2;
    const pickuptimes = new WarehouseCalendar(warehouse).pickupTimes({ includeNextOpenDay: true });

    // Add pickuptimes offset to start counting from `countFromDate`
    let dateOffset = 0;
    if (countFromDate) {
      while (pickuptimes[dateOffset]?.date && dayjs(pickuptimes[dateOffset]?.date).isBefore(countFromDate)) {
        dateOffset++;
      }
    }

    // Convert number of workdays to absolute number of days, startDay: Monday=0, Tuesday=1, etc
    let workDays = (n, startDay) => Math.floor((n + startDay) / 5) * 7 + ((n + startDay) % 5) - startDay;
    let getPickupTime = (day: number): Dayjs => {
      const dayWithOffset = Math.max(0, day + leadTimeOffset);
      const dayIndex = Math.max(0, dayWithOffset - 1 + leadTimeOffset) + dateOffset;
      return dayjs(pickuptimes[dayIndex]?.date ?? countFromDate).add(
        !pickuptimes[dayIndex] ? workDays(dayWithOffset, dayjs(countFromDate).isoWeekday() - 1) : 0,
        'days'
      );
    };

    const zipRegion = +(zipCode.match(/^[0-9]{2}/)?.[0] ?? 0);
    const { region } = useAccountStore();
    const leadTime = this.getLeadTime(isM3Order);
    if (
      region === 'EDS' &&
      this.deliveryCategory === 'Delivery' &&
      (zipRegion == 62 || (zipRegion >= 82 && zipRegion <= 99))
    ) {
      if (type === 'Date') {
        return getPickupTime(7);
      }
      const breakPoint = this.getBreakPoint(isM3Order);
      const isBeforeBreakPoint = breakPoint ? dayjs().isBefore(breakPoint) : false;
      return getPickupTime(isBeforeBreakPoint ? 4 : 5);
    }

    return getPickupTime(leadTime);
  }
}

class DeliveryOptions {
  private _ready: Promise<void>;
  private _resolveReady: (() => void) | null = null;
  // private deliveryOptions: DeliveryOption[] = [];
  private get deliveryOptions(): DeliveryOption[] {
    return (window as any).deliveryOptions ?? [];
  }
  private set deliveryOptions(value: DeliveryOption[]) {
    (window as any).deliveryOptions = value;
  }

  constructor() {
    this._ready = new Promise((resolve) => {
      this._resolveReady = resolve;
    });
    this.fetch();
  }

  private async fetch(): Promise<void> {
    const deliveryOptions = await getDeliveryOptions();
    this.deliveryOptions = deliveryOptions?.map((option) => new DeliveryOptionClass(option)) || [];

    if (this._resolveReady) {
      this._resolveReady();
    }
  }

  async ready(): Promise<void> {
    if (this._ready) {
      return this._ready;
    }
  }

  refresh(): Promise<void> {
    this._ready = new Promise((resolve) => {
      this._resolveReady = resolve;
    });
    return this.fetch();
  }

  getByTerms(terms: string): DeliveryOption {
    const options = this.deliveryOptions.filter((option) => option.deliveryTerms === terms);

    for (const o of options) {
      if (o.deliveryType == o.deliveryTerms) return o;
    }

    if (options.length === 0) {
      throw new Error(`Delivery option not found for terms: ${terms}`);
    }

    return options[0];
  }

  get(method: string, terms: string, deliveryCategories: string[] | null = null, exactMatch = false): DeliveryOption {
    let option = this.deliveryOptions.find(
      (option) =>
        option.deliveryTerms === terms &&
        option.deliveryType === method &&
        (deliveryCategories == null || deliveryCategories.includes(option.deliveryCategory))
    );

    if (!option && !exactMatch) {
      option = this.deliveryOptions.find((option) => option.deliveryTerms === terms && option.deliveryType === method);
    }

    if (!option && !exactMatch) {
      // If no direct match can be found we prioritize delivery terms
      option = this.getByTerms(terms);
    }

    if (!option) {
      throw new Error(`Delivery option not found for method: ${method}, terms: ${terms}`);
    }

    return option;
  }

  list(): DeliveryOption[] {
    return this.deliveryOptions;
  }
}

export default new DeliveryOptions();
